import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { booleanFromString, booleanString, formatPlainDate, InstantInterval } from '@airreading/common';
import {
  studentFeedbackIssueSchema,
  studentFeedbackStatusSchema,
  StudentSessionLike,
  StudentSessionFeedbackLike,
  StudentStatus,
  StudentStatusLabelMap,
  studentStatusSchema,
  StudentTag,
  studentTagLabel,
  StudentYear,
  StudentYearsInOrder,
  studentYearOption,
} from '@airreading/common/data';
import { selectOption } from '@/components/ui/multi-select-utils.js';
import { filterByStartInInterval } from './date-utils.js';
import { getTimeZone } from './time-zones.js';

export const allStudentStatuses = studentStatusSchema.options;

export const getStudentStatusLabel = (status: StudentStatus | null | undefined): string =>
  status ? StudentStatusLabelMap[status] : 'Unknown';

export const getStudentYearOptions = () =>
  Object.values(StudentYear).map((grade) => ({
    label: studentYearOption(grade),
    value: grade,
  }));

export const getStudentStatusOptions = () =>
  allStudentStatuses.map((status) => ({
    label: getStudentStatusLabel(status),
    value: status,
  }));

export const studentTagOptions = Object.values(StudentTag).map((tag) => ({
  label: studentTagLabel(tag),
  value: tag,
}));

export const buildStudentTagSelectOptions = (tags: StudentTag[] | null | undefined) =>
  tags?.map((tag) => ({
    label: studentTagLabel(tag),
    value: tag,
  })) ?? [];

export const getTotalFutureSessionsFilterOptions = () => [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
];

/**
 * Calculates the percentage of attended sessions from session feedbacks,
 * optionally filtered to only include sessions within the last N days.
 * Uses only feedback data for attendance calculation.
 */
export function calculateAttendanceRate(
  sessionFeedbacks: StudentSessionFeedbackLike[] | null,
  daysAgo?: number,
): number {
  if (!sessionFeedbacks?.length) {
    return 0;
  }

  // First, scope by optional date window (based on session start time)
  const windowedFeedbacks = !daysAgo
    ? sessionFeedbacks
    : filterByStartInInterval(sessionFeedbacks, InstantInterval.untilNow({ hours: daysAgo * 24 }));

  // Then, only include past sessions: session end time must have passed (never beyond NOW)
  const now = Temporal.Now.instant();
  const pastFeedbacks = windowedFeedbacks.filter(
    ({ endInstant, status }) => Temporal.Instant.compare(now, endInstant) >= 0 && status === 'completed',
  );

  if (!pastFeedbacks.length) {
    return 0;
  }

  const attendedSessions = pastFeedbacks.filter((feedback) => {
    // Only use feedback data for attendance calculation
    // Past sessions should have feedback from coaches
    return feedback.studentAttended === true;
  });
  const attendance = (attendedSessions.length / pastFeedbacks.length) * 100;
  return Math.round(attendance);
}

export function formatAttendedSessionDates(
  studentSessions: StudentSessionLike[] | null,
  preferredTimeZone: string | null | undefined,
): string | null {
  if (!studentSessions?.length) {
    return null;
  }

  const timeZone = getTimeZone(preferredTimeZone);
  return studentSessions
    .toSorted((a, b) => Temporal.Instant.compare(a.startInstant, b.startInstant))
    .flatMap(({ joined, startInstant }) => {
      const plainDate = startInstant.toZonedDateTimeISO(timeZone).toPlainDate();
      return !joined ? [] : [formatPlainDate(plainDate)];
    })
    .join(', ');
}

export function formatAttendedSessionDatesFromFeedback(
  sessionFeedbacks: StudentSessionFeedbackLike[] | null,
  preferredTimeZone: string | null | undefined,
): string | null {
  if (!sessionFeedbacks?.length) {
    return null;
  }

  const timeZone = getTimeZone(preferredTimeZone);
  return sessionFeedbacks
    .filter(({ status }) => status === 'completed')
    .toSorted((a, b) => Temporal.Instant.compare(a.startInstant, b.startInstant))
    .flatMap(({ studentAttended, startInstant }) => {
      const plainDate = startInstant.toZonedDateTimeISO(timeZone).toPlainDate();
      return studentAttended !== true ? [] : [formatPlainDate(plainDate)];
    })
    .join(', ');
}

export const formCreateStudentFeedbackSchema = z
  .object({
    studentFeedbackIssue: studentFeedbackIssueSchema,
    relevelNeeded: booleanString,
    learningPath: selectOption.optional(),
    startingLesson: selectOption.optional(),
    reviewer: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
    note: z.string().min(1, { message: 'Required' }),
    status: studentFeedbackStatusSchema,
  })
  .superRefine((data, ctx) => {
    if (booleanFromString(data.relevelNeeded)) {
      if (!data.learningPath?.value) {
        ctx.addIssue({ path: ['learningPath'], code: z.ZodIssueCode.custom, message: 'Required' });
      }
      if (!data.startingLesson?.value) {
        ctx.addIssue({ path: ['startingLesson'], code: z.ZodIssueCode.custom, message: 'Required' });
      }
    }
  });

export interface StudentWithQrCode {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullNameOrEmail?: string | null;
  shortCode?: string | null;
  grade: StudentYear | null;
}

function getStudentDisplayName(student: StudentWithQrCode): string {
  return student.fullNameOrEmail || `${student.firstName || ''} ${student.lastName || ''}`.trim() || '';
}

export type GroupedStudentsByGrade = {
  studentsByGrade: Map<StudentYear | 'no_grade', StudentWithQrCode[]>;
  orderedGrades: Array<StudentYear | 'no_grade'>;
};

/**
 * Groups students by grade level and sorts them alphabetically within each grade.
 * Filters out students without shortCode.
 *
 * @param students - Array of students with QR code information
 * @returns An object containing:
 *   - studentsByGrade: Map of grade to array of students (sorted alphabetically within each grade)
 *   - orderedGrades: Array of grades in order (Pre-K to 13th, then no_grade)
 * @throws Error if no students with short codes are found
 */
export function groupStudentsByGrade(students: StudentWithQrCode[]): GroupedStudentsByGrade {
  const studentsWithShortCode = students.filter((student) => student.shortCode);

  if (studentsWithShortCode.length === 0) {
    throw new Error(`No students with short codes to export. Total students provided: ${students.length}`);
  }

  const studentsByGrade = studentsWithShortCode.reduce((acc, student) => {
    const grade = student.grade || 'no_grade';
    const gradeStudents = acc.get(grade) || [];
    gradeStudents.push(student);
    acc.set(grade, gradeStudents);
    return acc;
  }, new Map<StudentYear | 'no_grade', StudentWithQrCode[]>());

  // Sort students within each grade alphabetically by name
  for (const gradeStudents of studentsByGrade.values()) {
    gradeStudents.sort((a, b) => {
      const nameA = getStudentDisplayName(a);
      const nameB = getStudentDisplayName(b);
      return nameA.localeCompare(nameB);
    });
  }

  // Process grades in order (Pre-K to 13th, then no_grade)
  const orderedGrades = [
    ...StudentYearsInOrder.filter((grade) => studentsByGrade.has(grade)),
    ...(studentsByGrade.has('no_grade') ? ['no_grade' as const] : []),
  ];

  return { studentsByGrade, orderedGrades };
}
